export function distributeBalancedExact(totalPhotos: number, totalPages: number = 24) {
  const templates = [1, 2, 3, 4, 5, 6, 7, 8, 9, 8, 10];

  // Step 1: base = largest template <= average
  const avg = totalPhotos / totalPages;
  const base = Math.max(...templates.filter(t => t <= avg));

  // Fill all with base
  const result = Array(totalPages).fill(base);

  // Step 2: Adjust remaining difference
  let currentTotal = result.reduce((a, b) => a + b, 0);
  let diff = totalPhotos - currentTotal;

  // If we need more photos, upgrade some pages
  while (diff > 0) {
    for (let i = 0; i < totalPages && diff > 0; i++) {
      const current = result[i];
      const nextIndex = templates.indexOf(current) + 1;
      if (nextIndex < templates.length) {
        const nextTemplate = templates[nextIndex];
        const gain = nextTemplate - current;
        if (gain <= diff) {
          result[i] = nextTemplate;
          diff -= gain;
        }
      }
    }
  }

  // If we put too many photos, downgrade some pages
  while (diff < 0) {
    for (let i = 0; i < totalPages && diff < 0; i++) {
      const current = result[i];
      const prevIndex = templates.indexOf(current) - 1;
      if (prevIndex >= 0) {
        const prevTemplate = templates[prevIndex];
        const loss = current - prevTemplate;
        if (loss <= Math.abs(diff)) {
          result[i] = prevTemplate;
          diff += loss;
        }
      }
    }
  }

  return result;
}

export function getImageOriginalSize(url: string): Promise<{ width: number; height: number; isValid: boolean }> {
  return new Promise((resolve) => {
    // Validate URL format first
    if (!url || typeof url !== 'string' || url.trim() === '') {
      console.warn(`Invalid photo URL provided: ${url}`);
      resolve({ width: 800, height: 600, isValid: false });
      return;
    }

    const img = new Image();
    
    // Set a timeout to avoid hanging on slow/broken URLs
    const timeout = setTimeout(() => {
      console.warn(`Image load timeout for URL: ${url}`);
      resolve({ width: 800, height: 600, isValid: false });
    }, 10000); // 10 second timeout

    img.onload = () => {
      clearTimeout(timeout);
      const width = img.naturalWidth || 800;
      const height = img.naturalHeight || 600;
      
      // Validate dimensions are reasonable
      if (width > 0 && height > 0 && width <= 10000 && height <= 10000) {
        resolve({ width, height, isValid: true });
      } else {
        console.warn(`Invalid image dimensions for URL: ${url} (${width}x${height})`);
        resolve({ width: 800, height: 600, isValid: false });
      }
    };

    img.onerror = (error) => {
      clearTimeout(timeout);
      console.warn(`Failed to load image: ${url}`, error);
      resolve({ width: 800, height: 600, isValid: false });
    };

    try {
      img.src = url;
    } catch (error) {
      clearTimeout(timeout);
      console.warn(`Error setting image source: ${url}`, error);
      resolve({ width: 800, height: 600, isValid: false });
    }
  });
}

export function validatePhotoUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  
  const trimmedUrl = url.trim();
  if (trimmedUrl === '') return false;
  
  // Check if it's a valid URL format
  try {
    new URL(trimmedUrl);
    return true;
  } catch {
    // If not a full URL, check if it's a valid relative path
    return trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./') || trimmedUrl.startsWith('../');
  }
}

export function processPhotoWithErrorHandling(
  photo: any, 
  onProgress?: (processed: number, total: number, url: string) => void,
  index?: number,
  total?: number
): Promise<{ photo: any; size: { width: number; height: number; isValid: boolean }; error?: string }> {
  return new Promise(async (resolve) => {
    try {
      if (!photo || !photo.pictureURL) {
        resolve({ 
          photo, 
          size: { width: 800, height: 600, isValid: false }, 
          error: 'Invalid photo object or missing pictureURL' 
        });
        return;
      }

      const srcUrl = photo.pictureURL;
      
      // Validate URL before processing
      if (!validatePhotoUrl(srcUrl)) {
        resolve({ 
          photo, 
          size: { width: 800, height: 600, isValid: false }, 
          error: `Invalid photo URL format: ${srcUrl}` 
        });
        return;
      }

      // Report progress if callback provided
      if (onProgress && typeof index === 'number' && typeof total === 'number') {
        onProgress(index, total, srcUrl);
      }

      // Get image size with error handling
      const size = await getImageOriginalSize(srcUrl);
      
      resolve({ photo, size });
    } catch (error) {
      console.error('Error processing photo:', error);
      resolve({ 
        photo, 
        size: { width: 800, height: 600, isValid: false }, 
        error: error instanceof Error ? error.message : 'Unknown error processing photo' 
      });
    }
  });
}

export function distributePhotosToPages(pages: any, totalPhotos: number) {
  // Get pages sorted by position (ignore negative positions)
  const sorted: any = Object.entries(pages)
    .filter(([_, p]: any) => p.position >= 0)
    .sort((a: any, b: any) => a[1].position - b[1].position);

  const result: any = {};
  const totalPages = sorted.length;
  const totalCapacity = sorted.reduce((sum: any, [, p]: any) => sum + p.count, 0);

  let remaining = totalPhotos - totalCapacity;

  // Clone base
  for (const [id, page] of sorted) {
    result[id] = { ...page };
  }

  // Keep distributing until we reach totalPhotos
  while (remaining > 0) {
    // Sort pages by current count ascending, then by position
    sorted.sort((a: any, b: any) => {
      const aCount = result[a[0]].count;
      const bCount = result[b[0]].count;
      if (aCount === bCount) return a[1].position - b[1].position;
      return aCount - bCount;
    });

    for (const [id] of sorted) {
      if (remaining <= 0) break;
      result[id].count += 1;
      remaining--;
    }
  }

  // Reattach pages with position < 0 (untouched)
  for (const [id, page] of Object.entries(pages) as any) {
    if (page.position < 0) result[id] = { ...page };
  }

  return result;
}
